import { motion } from 'framer-motion';
import type { CSSProperties, FunctionComponent } from 'react';

const treeConnectorOffset = 'calc(-1 * var(--spacing-4xl))';
const verticalDottedGradient =
    'repeating-linear-gradient(transparent 0px, transparent 2px, var(--color-gray-500) 2px, var(--color-gray-500) 5px)';
const horizontalDottedGradient =
    'repeating-linear-gradient(to right, transparent 0px, transparent 2px, var(--color-gray-500) 2px, var(--color-gray-500) 5px)';

/**
 * The dotted line that ties a nested board card to its parent: down from the card above and across into this one.
 * Render it inside a `relative` wrapper around the nested card, in a `CardCollapsibleContent`. `elbowTop` places the
 * turn into the card when the wrapper also holds the card's own children; it defaults to the wrapper's middle.
 */
export const BoardTreeConnector: FunctionComponent<{
    readonly elbowTop?: string;
    readonly isLast: boolean;
    readonly testId?: string;
}> = ({ elbowTop = '50%', isLast, testId }) => {
    const verticalLineStyle: CSSProperties = {
        background: verticalDottedGradient,
        bottom: isLast ? `calc(100% - ${elbowTop})` : 'calc(-1 * var(--spacing-s) / 2)',
        left: treeConnectorOffset,
        top: 'calc(-1 * var(--spacing-s) / 2)',
        width: 1,
    };

    return (
        <motion.div
            aria-hidden={true}
            animate={{ opacity: 1 }}
            className="pointer-events-none absolute inset-0"
            data-testid={testId}
            initial={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
        >
            <span className="absolute" style={verticalLineStyle} />
            <span
                className="absolute h-px -translate-y-1/2"
                style={{
                    background: horizontalDottedGradient,
                    left: treeConnectorOffset,
                    top: elbowTop,
                    width: `calc(-1 * ${treeConnectorOffset})`,
                }}
            />
        </motion.div>
    );
};
